import React, { useState, useEffect } from 'react';
import request from "../../util/SuperagentUtils";
import Busy from "../../components/Busy";
import {useSnackbar} from "../../context/snackbar-context";
import { getErrorMessageForNonStandardAndStandardResponse } from "../../util/NetworkErrorUtil";
import { BuyerNotificationSettingsVO } from "../../types/model/valueObject/notificationSettings/BuyerNotificationSettingsVO";
import { BuyerNotificationSettingRecipientRoleVO } from "../../types/model/valueObject/notificationSettings/BuyerNotificationSettingRecipientRoleVO";
import { useReactiveGetRequest } from "../../hooks/request/useReactiveGetRequest";
import { notificationEndpoints } from "../../requests/api-endpoints";
import { NotificationSettingsPanel } from "../../components/notifications/NotificationSettingsPanel";
import {
  NotificationRoleDefinitionType,
  NotificationChannelDefinitionType,
  NotificationCategoryDefinitionType,
  NotificationTypeChannels,
  applyOwnerFallback,
  buildNotificationSettingsFromData,
  serializeRecipientRoles,
  NotificationChannelsDefinitions
} from "../../util/NotificationUtil";

const BuyerChannels: NotificationChannelDefinitionType[] = [
  NotificationChannelsDefinitions.EMAIL,
  NotificationChannelsDefinitions.SMS
];

const BuyerCategories: NotificationCategoryDefinitionType[] = [
  {
    value: 'ADMINISTRATIVE',
    label: 'Administrative',
    tooltip: 'Receive notifications for administrative updates, such as changes to your account or billing information',
  },
  {
    value: 'FLEET_MANAGEMENT',
    label: 'Fleet Management',
    tooltip: 'Receive notifications related to fleet management, such as vehicle assignments or changes to your fleet',
  },
  {
    value: 'OVERAGE_ALERTS',
    label: 'Overage Alerts',
    tooltip: 'Receive notifications when your usage exceeds certain thresholds, such as parking',
  },
];

// First role is the guaranteed recipient (Account Owner) — see the fallback rule
// in NotificationUtil. The `sub` lines are descriptive labels shown in the matrix.
const BuyerRoles: NotificationRoleDefinitionType[] = [
  {value: 'PRIMARY_OWNER', label: 'Account Owner', sub: 'Primary account holder'},
  {value: 'OWNER', label: 'Administrator', sub: 'Manages users & billing'},
  {value: 'DISPATCHER', label: 'Dispatcher', sub: 'Coordinates fleet activity'},
];

const BuyerNotificationSettings = () => {
  const { snackbarShowMessage } = useSnackbar();

  const { data } = useReactiveGetRequest<BuyerNotificationSettingsVO>({ requestUrl: notificationEndpoints.buyer.get, shouldAbort: true });

  const [notificationSettings, setNotificationSettings] = useState<NotificationTypeChannels>();
  const [changesMade, setChangesMade] = useState<boolean>(false);

  useEffect(() => {
    // Leaves state undefined until the GET resolves, so the matrix never flashes
    // the all-default (owner-locked) state before the saved data arrives.
    setNotificationSettings(buildNotificationSettingsFromData(data, BuyerChannels, BuyerCategories, BuyerRoles));
  }, [data]);

  const onSubmit = () => {
    if (!notificationSettings) return;
    Busy.set(true);

    // Materialise the Account Owner fallback so the saved payload always keeps
    // at least one recipient per (channel, category).
    const effectiveSettings = applyOwnerFallback(notificationSettings, BuyerRoles);
    const recipientRoles: BuyerNotificationSettingRecipientRoleVO[] = serializeRecipientRoles(effectiveSettings) as unknown as BuyerNotificationSettingRecipientRoleVO[];

    const payload: BuyerNotificationSettingsVO = {
      recipientRoles
    } as BuyerNotificationSettingsVO;

    request
      .patch(notificationEndpoints.buyer.patch)
      .send(payload)
      .then(() => {
        setChangesMade(false);
        Busy.set(false);
        snackbarShowMessage('Notifications settings saved successfully!', 'success', 8000);
      }).catch(error => {
        Busy.set(false);
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(error), 'error', 15000);
      });
  }

  const handleNotificationSettingsChange = (channel: string, category: string, role: string, value: boolean) => {
    setNotificationSettings(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        [channel]: {
          ...prev[channel],
          [category]: {
            ...prev[channel][category],
            [role]: value,
          },
        },
      };
    });
    setChangesMade(true);
  };

  return (
    <NotificationSettingsPanel
      roles={BuyerRoles}
      channels={BuyerChannels}
      categories={BuyerCategories}
      settings={notificationSettings}
      onToggle={handleNotificationSettingsChange}
      changesMade={changesMade}
      onSave={onSubmit}
    />
  );
}

export { BuyerNotificationSettings };
